// FILE: src/App.js
import React, { useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";

import TenantRoute from "./routes/TenantRoute";
import Unauthorized from "./pages/Unauthorized";

import SignUp from "./components/Signup";
import Login from "./components/Login";
import Home from "./components/Home";
import AddMember from "./components/AddMember";
import Members from "./components/Members";
import Dashboard from "./components/Dashboard";
import Events from "./components/Events.jsx";
import EventsAdmin from "./components/EventsAdmin";
import AdminRoute from "./components/AdminRoute";
import AdminDevotionals from "./components/AdminDevotionals";
import MemberDevotionals from "./components/MemberDevotionals";
import Chatbot from "./components/Chatbot";
import Attendance from "./components/attendance";
import UploadContent from "./components/UploadContent";
import ContentList from "./components/ContentList";
import MemberProfile from "./components/MemberProfile";
import AdminAttendanceReport from "./components/AdminAttendanceReport";
import Give from "./components/Give"; // ✅ ADD THIS

import { useAuth } from "./context/AuthContext";

import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

// ✅ Put your logo file here: src/assets/equip-foundation.png
import EquipLogo from "./assets/equip-foundation.png";

function App() {
  const { user, role, loading, logout, tenantConfig } = useAuth();
  const [profile, setProfile] = useState(null);

  const PUBLIC_BRAND_NAME = "The Equip Foundation";
  const brandName = tenantConfig?.name || PUBLIC_BRAND_NAME;

  // Load Firestore profile so we can display firstName
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setProfile(snap.data());
        else setProfile(null);
      } catch (err) {
        console.error("Failed to load user profile in App:", err);
      }
    };

    loadProfile();
  }, [user]);

  if (loading) return <p className="p-6 text-center">Loading…</p>;

  const handleLogout = async () => {
    try {
      if (typeof logout === "function") await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      window.location.href = "/";
    }
  };

  // Prefer firstName, then displayName, then email
  const displayName =
    profile?.firstName ||
    user?.displayName ||
    (user?.email ? user.email : "");

  // Tenant feature flags
  const enabledMenus = tenantConfig?.enabledMenus ?? [];
  const isEnabled = (id) => enabledMenus.includes(id);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-slate-600 text-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
              <img
                src={EquipLogo}
                alt="The Equip Foundation"
                className="h-8 w-8 object-contain"
              />
            </div>

            <div className="font-semibold">{brandName}</div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-3 text-sm">
            {/* Public */}
            <Link
              to="/"
              className="px-3 py-1.5 rounded-full hover:bg-slate-600"
            >
              Home
            </Link>
            <Link
              to="/events"
              className="px-3 py-1.5 rounded-full hover:bg-slate-600"
            >
              Events
            </Link>
            <Link
              to="/content"
              className="px-3 py-1.5 rounded-full hover:bg-slate-600"
            >
              Content
            </Link>

            {/* ✅ GIVE (public) */}
            <Link
              to="/give"
              className="px-3 py-1.5 rounded-full hover:bg-slate-600"
            >
              Give
            </Link>

            {/* Logged-in (tenant-aware) */}
            {user && isEnabled("devotionals") && (
              <Link
                to="/devotionals"
                className="px-3 py-1.5 rounded-full hover:bg-slate-600"
              >
                Devotionals
              </Link>
            )}

            {user && role === "admin" && isEnabled("admin.devotionals") && (
              <Link
                to="/admin/devotionals"
                className="px-3 py-1.5 rounded-full hover:bg-slate-600"
              >
                Manage Devotionals
              </Link>
            )}

            {user && isEnabled("chat") && (
              <Link
                to="/chat"
                className="px-3 py-1.5 rounded-full hover:bg-slate-600"
              >
                Chat
              </Link>
            )}

            {/* Member-only */}
            {user && role === "member" && isEnabled("profile") && (
              <Link
                to="/profile"
                className="px-3 py-1.5 rounded-full hover:bg-slate-600"
              >
                My Profile
              </Link>
            )}

            {/* Admin-only menus */}
            {user && role === "admin" && (
              <>
                {isEnabled("admin.events") && (
                  <Link
                    to="/admin/events"
                    className="px-3 py-1.5 rounded-full hover:bg-slate-600"
                  >
                    Manage Events
                  </Link>
                )}
                {isEnabled("admin.members") && (
                  <Link
                    to="/members"
                    className="px-3 py-1.5 rounded-full hover:bg-slate-600"
                  >
                    Members
                  </Link>
                )}
                {isEnabled("admin.addMember") && (
                  <Link
                    to="/add-member"
                    className="px-3 py-1.5 rounded-full hover:bg-slate-600"
                  >
                    Add Member
                  </Link>
                )}
                {isEnabled("admin.attendance") && (
                  <Link
                    to="/attendance"
                    className="px-3 py-1.5 rounded-full hover:bg-slate-600"
                  >
                    Attendance
                  </Link>
                )}
                {isEnabled("admin.attendanceReport") && (
                  <Link
                    to="/admin/attendance-report"
                    className="px-3 py-1.5 rounded-full hover:bg-slate-600"
                  >
                    Attendance Analytics
                  </Link>
                )}
                {isEnabled("admin.dashboard") && (
                  <Link
                    to="/dashboard"
                    className="px-3 py-1.5 rounded-full hover:bg-slate-600"
                  >
                    Dashboard
                  </Link>
                )}
              </>
            )}

            {/* Logged out */}
            {!user && (
              <>
                <Link
                  to="/signup"
                  className="px-3 py-1.5 rounded-full bg-white/10"
                >
                  Signup
                </Link>
                <Link
                  to="/login"
                  className="px-3 py-1.5 rounded-full border border-white/40"
                >
                  Login
                </Link>
              </>
            )}
          </nav>

          {/* Right side: user info + logout */}
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex flex-col text-right">
                <span className="text-xs text-indigo-100">Signed in as</span>
                <span className="text-sm font-medium">
                  {displayName}{" "}
                  <span className="text-xs text-indigo-100">
                    ({role ?? "member"})
                  </span>
                </span>
              </div>
            )}

            {user && (
              <button
                onClick={handleLogout}
                className="text-xs sm:text-sm px-3 py-1.5 rounded-full bg-white text-indigo-600 font-medium"
              >
                Logout
              </button>
            )}
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-slate-600/40">
          <div className="flex overflow-x-auto no-scrollbar px-2 py-2 gap-2 text-xs">
            <Link
              to="/"
              className="px-3 py-1 rounded-full bg-slate-600 text-white whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/events"
              className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
            >
              Events
            </Link>
            <Link
              to="/content"
              className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
            >
              Content
            </Link>

            {/* ✅ GIVE (mobile) */}
            <Link
              to="/give"
              className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
            >
              Give
            </Link>

            {user && isEnabled("devotionals") && (
              <Link
                to="/devotionals"
                className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
              >
                Devotionals
              </Link>
            )}

            {user && isEnabled("chat") && (
              <Link
                to="/chat"
                className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
              >
                Chat
              </Link>
            )}

            {user && role === "member" && isEnabled("profile") && (
              <Link
                to="/profile"
                className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
              >
                My Profile
              </Link>
            )}

            {user && role === "admin" && isEnabled("admin.events") && (
              <Link
                to="/admin/events"
                className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
              >
                Manage Events
              </Link>
            )}

            {user && role === "admin" && isEnabled("admin.dashboard") && (
              <Link
                to="/dashboard"
                className="px-3 py-1 rounded-full bg-slate-600/40 text-white whitespace-nowrap"
              >
                Dashboard
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <Routes>
              {/* Utility */}
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/events" element={<Events />} />
              <Route path="/content" element={<ContentList />} />
              <Route path="/give" element={<Give />} /> {/* ✅ ADD THIS */}
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<Login />} />

              {/* Logged-in (tenant protected) */}
              <Route
                path="/devotionals"
                element={
                  <TenantRoute menuId="devotionals">
                    <MemberDevotionals />
                  </TenantRoute>
                }
              />

              <Route
                path="/chat"
                element={
                  <TenantRoute menuId="chat">
                    <Chatbot />
                  </TenantRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <TenantRoute menuId="profile">
                    <MemberProfile />
                  </TenantRoute>
                }
              />

              {/* Admin-only */}
              <Route
                path="/dashboard"
                element={
                  <TenantRoute menuId="admin.dashboard">
                    <AdminRoute>
                      <Dashboard />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/add-member"
                element={
                  <TenantRoute menuId="admin.addMember">
                    <AdminRoute>
                      <AddMember />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/members"
                element={
                  <TenantRoute menuId="admin.members">
                    <AdminRoute>
                      <Members />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/admin/events"
                element={
                  <TenantRoute menuId="admin.events">
                    <AdminRoute>
                      <EventsAdmin />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/admin/devotionals"
                element={
                  <TenantRoute menuId="admin.devotionals">
                    <AdminRoute>
                      <AdminDevotionals />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/admin/content/upload"
                element={
                  <TenantRoute menuId="admin.uploadContent">
                    <AdminRoute>
                      <UploadContent onUploaded={() => {}} />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/attendance"
                element={
                  <TenantRoute menuId="admin.attendance">
                    <AdminRoute>
                      <Attendance />
                    </AdminRoute>
                  </TenantRoute>
                }
              />

              <Route
                path="/admin/attendance-report"
                element={
                  <TenantRoute menuId="admin.attendanceReport">
                    <AdminRoute>
                      <AdminAttendanceReport />
                    </AdminRoute>
                  </TenantRoute>
                }
              />
            </Routes>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-3 text-xs text-slate-500 flex justify-between">
          <span>
            © {new Date().getFullYear()} {brandName}
          </span>
          <span>Built for ministry & learning</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
